import {
    Injectable, inject, DestroyRef
} from "@angular/core";
import {
    Router, NavigationStart, NavigationEnd, NavigationError
} from "@angular/router";
import {
    filter
} from "rxjs";
import Rollbar from "rollbar";
import { CoreUiException } from "../../exceptions/core-ui-exception";
import { ICommonConfig } from "../../application/interfaces/common-config";
import { COMMON_APP_CONFIG } from "../../application/constants/app-tokens";
import { type BrowserAgent } from "@newrelic/browser-agent";
import { InteractionInstance } from "@newrelic/browser-agent/src/loaders/api/interaction-types.js";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { LogLevel } from "./enums/log-level.enum";

declare const newrelic: BrowserAgent;

/**
 * The subset of the Network Information API reported with memory snapshots.
 */
interface INetworkConnection {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
}

/**
 * Severity ranking used to decide whether a log call reaches a remote sink (Rollbar or New Relic).
 * A call is forwarded when the configured level's rank is >= the call's rank.
 */
const LOG_LEVEL_RANK: Record<LogLevel, number> = {
    [LogLevel.Off]: 0,
    [LogLevel.Error]: 1,
    [LogLevel.Warning]: 2,
    [LogLevel.Info]: 3,
    [LogLevel.Debug]: 4
};

/**
 * Service for centralized logging that supports console output, Rollbar and New Relic Browser log forwarding.
 *
 * This service provides structured logging capabilities with automatic error tracking
 * through Rollbar when configured, and performance monitoring through New Relic Browser Agent.
 * It handles different log levels (error, info, warn, debug), processes CoreUiException
 * objects to extract nested original errors, and provides methods for tracking user
 * interactions, custom actions, memory snapshots, user and context attributes, and large-response
 * observation via New Relic.
 *
 * @example
 * ```typescript
 * constructor(private loggingService: LoggingService) {}
 *
 * // Log an error
 * this.loggingService.logError('User authentication failed', error);
 *
 * // Log info with multiple arguments
 * this.loggingService.logInfo('User logged in', { userId: 123, timestamp: new Date() });
 * ```
 */
@Injectable({
    providedIn: "root"
})
export class LoggingService {

    /**
     * Maps a {@link LogLevel} to the New Relic log level name.
     */
    private static readonly _NR_LEVEL: Record<LogLevel, "ERROR" | "WARN" | "INFO" | "DEBUG" | undefined> = {
        [LogLevel.Off]: undefined,
        [LogLevel.Error]: "ERROR",
        [LogLevel.Warning]: "WARN",
        [LogLevel.Info]: "INFO",
        [LogLevel.Debug]: "DEBUG"
    };

    /**
     * Maximum length of a serialized object sent as a New Relic custom attribute.
     */
    private static readonly _MAX_ATTRIBUTE_LENGTH = 2048;

    /**
     * Rollbar instance for remote error tracking and logging.
     * Only initialized if the configured Rollbar level is not {@link LogLevel.Off}.
     */
    private readonly _rollbar?: Rollbar;

    /**
     * Application configuration containing Rollbar and New Relic settings and app version.
     */
    private readonly _appConfig = inject<ICommonConfig>(COMMON_APP_CONFIG);

    /**
     * Whether the New Relic Browser Agent is active and available for telemetry.
     */
    private readonly _nrActive: boolean;

    /**
     * Map of tracked interactions keyed by unique interaction ID.
     */
    private readonly _interactions = new Map<string, InteractionInstance>();

    /**
     * Used to clean up route subscriptions and the resource timing observer when the service is destroyed.
     */
    private readonly _destroyRef = inject(DestroyRef);

    /**
     * The Angular Router, when available. Used for route tracking and the route log attribute.
     */
    private readonly _router = inject(Router, { optional: true });

    /**
     * Initializes the LoggingService and configures Rollbar and New Relic if enabled.
     *
     * Sets up Rollbar with the following configuration:
     * - Disables automatic uncaught exception capture
     * - Configures source map support for better error tracking
     * - Sets environment and version information from app config
     *
     * After Rollbar initialization, checks for the New Relic Browser Agent and
     * optionally subscribes to Angular Router events for route change tracking. When New Relic is
     * active, the application version is also registered with the agent.
     */
    constructor() {

        if (this._appConfig.rollbar && this._appConfig.rollbar.level !== LogLevel.Off) {
            this._rollbar = new Rollbar({
                accessToken: this._appConfig.rollbar.accessToken,
                captureUncaught: false,
                captureUnhandledRejections: false,
                autoInstrument: false,
                payload: {
                    environment: this._appConfig.rollbar.environment,
                    client: {
                        javascript: {

                            code_version: this._appConfig.appVersion,

                            source_map_enabled: true,

                            guess_uncaught_frames: true
                        }
                    }
                }
            });
        }

        // If you don't check newrelic global package this way an exception is thrown
        this._nrActive = this._appConfig.newrelic?.enabled && typeof newrelic !== "undefined";

        if (this._appConfig.newrelic?.enabled && !this._nrActive) {
            this.logError("New Relic is enabled in config but the browser agent is not loaded");
        }

        if (this._nrActive) {
            newrelic.setApplicationVersion(this._appConfig.appVersion);
        }

        if (this._appConfig.newrelic?.trackRouteChanges) {
            if (!this._nrActive) {
                this.logWarn("Route tracking is enabled but New Relic is not active");
            }

            if (!this._router) {
                this.logError("Route tracking is enabled but the Angular Router is not available");
            }
            else {
                this._setupRouteTracking(this._router);
            }
        }
    }

    /**
     * Logs error messages to console, Rollbar and New Relic Browser (each only when configured and enabled for this level).
     *
     * Processes CoreUiException objects to extract nested original errors
     * and includes them in the log output. Handles logging failures gracefully
     * by falling back to console.error.
     *
     * When New Relic forwarding is enabled for the Error level, each Error (including nested
     * original errors) is also reported with `noticeError`.
     *
     * @param loggingArguments - Variable number of arguments to log (strings, objects, errors, etc.)
     *
     * @example
     * ```typescript
     * this.loggingService.logError('Database connection failed', error, { userId: 123 });
     * ```
     */
    logError(...loggingArguments: Array<unknown>): void {

        try {

            const fields: Array<Rollbar.LogArgument> = this._processLoggingArguments(loggingArguments);

            console.error(...fields);

            if (this._shouldLogToRollbar(LogLevel.Error)) {
                this._rollbar.error(...fields);
            }

            this._logToNewRelic(LogLevel.Error, loggingArguments);
        }
        catch (error) {

            console.error("Logger failed", error, loggingArguments);
        }
    }

    /**
     * Logs informational messages to console, Rollbar and New Relic Browser (each only when configured and enabled for this level).
     *
     * Used for general application flow information and non-error events.
     * Processes arguments to extract nested errors from CoreUiException objects.
     *
     * @param loggingArguments - Variable number of arguments to log (strings, objects, etc.)
     *
     * @example
     * ```typescript
     * this.loggingService.logInfo('User action completed', { action: 'save', duration: 150 });
     * ```
     */
    logInfo(...loggingArguments: Array<unknown>): void {

        const fields: Array<Rollbar.LogArgument> = this._processLoggingArguments(loggingArguments);

        console.log(...fields);

        if (this._shouldLogToRollbar(LogLevel.Info)) {
            this._rollbar.info(...fields);
        }

        this._logToNewRelic(LogLevel.Info, loggingArguments);
    }

    /**
     * Logs warning messages to console, Rollbar and New Relic Browser (each only when configured and enabled for this level).
     *
     * Used for potentially problematic situations that don't prevent
     * application functionality but should be monitored.
     *
     * @param loggingArguments - Variable number of arguments to log (strings, objects, etc.)
     *
     * @example
     * ```typescript
     * this.loggingService.logWarn('API response time exceeded threshold', { responseTime: 5000 });
     * ```
     */
    logWarn(...loggingArguments: Array<unknown>): void {

        const fields: Array<Rollbar.LogArgument> = this._processLoggingArguments(loggingArguments);

        console.warn(...fields);

        if (this._shouldLogToRollbar(LogLevel.Warning)) {
            this._rollbar.warn(...fields);
        }

        this._logToNewRelic(LogLevel.Warning, loggingArguments);
    }

    /**
     * Logs debug messages to console, Rollbar and New Relic Browser (each only when configured and enabled for this level).
     *
     * Used for detailed diagnostic information during development and debugging.
     * Should be used sparingly in production environments.
     *
     * @param loggingArguments - Variable number of arguments to log (strings, objects, etc.)
     *
     * @example
     * ```typescript
     * this.loggingService.logDebug('Component state changed', { oldState, newState });
     * ```
     */
    logDebug(...loggingArguments: Array<unknown>): void {

        const fields: Array<Rollbar.LogArgument> = this._processLoggingArguments(loggingArguments);

        console.debug(...fields);

        if (this._shouldLogToRollbar(LogLevel.Debug)) {
            this._rollbar.debug(...fields);
        }

        this._logToNewRelic(LogLevel.Debug, loggingArguments);
    }

    /**
     * Starts a New Relic Browser interaction for tracking user-initiated workflows.
     *
     * Creates a named interaction via the New Relic Browser Agent and tracks it
     * internally using a unique ID. The interaction must be ended by calling
     * {@link endInteraction} with the returned ID. If New Relic is not active,
     * the ID is still returned for consistent caller behavior, but no interaction
     * is created.
     *
     * @param name - The name to assign to the interaction (e.g., "SaveCampaign", "SearchProducts")
     * @returns A unique interaction ID to pass to {@link endInteraction}
     *
     * @example
     * ```typescript
     * const id = this.loggingService.startInteraction("SaveCampaign");
     * await this.saveCampaign();
     * this.loggingService.endInteraction(id);
     * ```
     */
    startInteraction(name: string): string {
        const id = `${name}:${crypto.randomUUID()}`;
        this.logDebug(`[NR] startInteraction: name=${name}, id=${id}`);

        if (this._nrActive) {
            const interaction = newrelic.interaction()
                .setName(name)
                .save();
            this._interactions.set(id, interaction);
        }

        return id;
    }

    /**
     * Ends a previously started New Relic Browser interaction.
     *
     * Looks up the interaction by ID, calls end() on it if found, and removes it
     * from the internal tracking map. Logs a warning if the ID is not found.
     *
     * @param id - The interaction ID returned by {@link startInteraction}
     *
     * @example
     * ```typescript
     * const id = this.loggingService.startInteraction("SaveCampaign");
     * await this.saveCampaign();
     * this.loggingService.endInteraction(id);
     * ```
     */
    endInteraction(id: string): void {
        this.logDebug(`[NR] endInteraction: id=${id}`);

        const interaction = this._interactions.get(id);

        if (interaction) {
            interaction.end();
            this._interactions.delete(id);
        }
        else if (this._nrActive) {
            this.logWarn(`[NR] endInteraction called with unknown id: ${id}`);
        }
    }

    /**
     * Records a custom page action in New Relic with automatic performance metadata.
     *
     * Always logs the action via logDebug. If New Relic is active, also sends a named
     * event to New Relic via addPageAction, automatically enriching the provided attributes
     * with a high-resolution timestamp, current JavaScript heap memory usage (if available
     * via the Performance Memory API), and the application version.
     *
     * @param name - The action name to record (e.g., "FilterApplied", "ExportStarted")
     * @param attributes - Additional key-value pairs to attach to the action event
     *
     * @example
     * ```typescript
     * this.loggingService.recordAction("FilterApplied", { filterType: "category", resultCount: 42 });
     * ```
     */
    recordAction(name: string, attributes: Record<string, any> = {}): void {
        const enriched = {
            ...attributes,
            timestamp: performance.now(),
            memoryUsed: (performance as any).memory?.usedJSHeapSize,
            appVersion: this._appConfig.appVersion,
        };

        this.logDebug(`[NR] recordAction: ${name}`, enriched);

        if (this._nrActive) {
            newrelic.addPageAction(name, enriched);
        }
    }

    /**
     * Records a memory usage snapshot in New Relic for tracking JavaScript heap metrics.
     *
     * Always logs the snapshot via logDebug. If New Relic is active, also captures the
     * current state of the JavaScript heap via the Performance Memory API and sends it
     * to New Relic as a "MemorySnapshot" page action with the application version, plus the
     * network `effectiveType`, `downlink` and `rtt` when `navigator.connection` exists. This
     * is a no-op if the Performance Memory API is not available (non-Chromium browsers).
     *
     * @param context - A label describing when/why this snapshot was taken (e.g., "AfterBulkImport", "OnRouteChange")
     *
     * @example
     * ```typescript
     * this.loggingService.recordMemorySnapshot("AfterBulkImport");
     * ```
     */
    recordMemorySnapshot(context: string): void {
        const mem = (performance as any).memory;
        if (mem) {
            const connection = (navigator as unknown as {connection?: INetworkConnection}).connection;
            const snapshot = {
                context,
                usedJSHeapSize: mem.usedJSHeapSize,
                totalJSHeapSize: mem.totalJSHeapSize,
                jsHeapSizeLimit: mem.jsHeapSizeLimit,
                appVersion: this._appConfig.appVersion,
                ...(connection && {
                    effectiveType: connection.effectiveType,
                    downlink: connection.downlink,
                    rtt: connection.rtt
                })
            };

            this.logDebug(`[NR] memorySnapshot: ${context}`, snapshot);

            if (this._nrActive) {
                newrelic.addPageAction("MemorySnapshot", snapshot);
            }
        }
    }

    /**
     * Associates the current New Relic session with a user id. No-op when New Relic is not active.
     *
     * @param userId - The id of the signed-in user
     *
     * @example
     * ```typescript
     * this.loggingService.setUser(user.id);
     * ```
     */
    setUser(userId: string): void {
        if (this._nrActive) {
            newrelic.setUserId(userId);
        }
    }

    /**
     * Removes the user id from the New Relic session (for example on sign-out). No-op when New Relic is not active.
     *
     * @example
     * ```typescript
     * this.loggingService.clearUser();
     * ```
     */
    clearUser(): void {
        if (this._nrActive) {
            newrelic.setUserId(null);
        }
    }

    /**
     * Sets custom attributes that New Relic attaches to every subsequent event until cleared with
     * {@link clearContext}. Attributes persist across pages. An `undefined` value is stored as `null`.
     * No-op when New Relic is not active.
     *
     * @param attributes - Attribute names mapped to their values
     *
     * @example
     * ```typescript
     * this.loggingService.setContext({ taskId: task.id, taskName: task.name });
     * ```
     */
    setContext(attributes: Record<string, string | number | undefined>): void {
        if (this._nrActive) {
            Object.entries(attributes).forEach(([name, value]) => newrelic.setCustomAttribute(name, value ?? null, true));
        }
    }

    /**
     * Clears custom attributes previously set with {@link setContext} by setting them to `null`.
     * No-op when New Relic is not active.
     *
     * @param names - The attribute names to clear
     *
     * @example
     * ```typescript
     * this.loggingService.clearContext(["taskId", "taskName"]);
     * ```
     */
    clearContext(names: Array<string>): void {
        if (this._nrActive) {
            names.forEach((name) => newrelic.setCustomAttribute(name, null, true));
        }
    }

    /**
     * Watches resource timing for responses larger than a threshold and records a "LargeResponse"
     * page action (via {@link recordAction}) for each one.
     *
     * Only resources loaded after this call are observed. A resource matches when its URL contains
     * `urlFragment` and its `decodedBodySize` is at least `minBytes`. The observer is disconnected when
     * the service is destroyed. No-op when New Relic is not active or `PerformanceObserver` is unavailable.
     *
     * Note: cross-origin responses report sizes of 0 unless the server sends a `Timing-Allow-Origin` header,
     * so they never exceed the threshold.
     *
     * @param urlFragment - Text the resource URL must contain
     * @param minBytes - Minimum decoded body size in bytes to record
     *
     * @example
     * ```typescript
     * this.loggingService.observeLargeResponses("data-entry-deal", 500 * 1024);
     * ```
     */
    observeLargeResponses(urlFragment: string, minBytes: number): void {
        if (!this._nrActive || typeof PerformanceObserver === "undefined") {
            return;
        }

        const observer = new PerformanceObserver((list) => {
            list.getEntries()
                .filter((entry): entry is PerformanceResourceTiming => entry.name.includes(urlFragment))
                .filter((entry) => entry.decodedBodySize >= minBytes)
                .forEach((entry) => this.recordAction("LargeResponse", {
                    url: entry.name,
                    transferSize: entry.transferSize,
                    encodedBodySize: entry.encodedBodySize,
                    decodedBodySize: entry.decodedBodySize,
                    ttfbMs: Math.round(entry.responseStart - entry.requestStart),
                    downloadMs: Math.round(entry.responseEnd - entry.responseStart)
                }));
        });
        observer.observe({
            type: "resource",
            buffered: false
        });
        this._destroyRef.onDestroy(() => observer.disconnect());
    }

    /**
     * Sums the decoded body size of resources whose URL contains a fragment and that started at or after a time.
     *
     * Reads the Resource Timing API regardless of whether New Relic is active. Note: cross-origin
     * responses report sizes of 0 unless the server sends a `Timing-Allow-Origin` header.
     *
     * @param urlFragment - Text the resource URL must contain
     * @param startTime - A `performance.now()` value; only resources with `startTime` at or after it count
     * @returns The total decoded bytes, or `undefined` when no resource matches or all report 0
     *
     * @example
     * ```typescript
     * const start = performance.now();
     * await this.search();
     * const bytes = this.loggingService.resourceBytesSince("data-entry-deal", start);
     * ```
     */
    resourceBytesSince(urlFragment: string, startTime: number): number | undefined {
        const total = (performance.getEntriesByType("resource") as Array<PerformanceResourceTiming>)
            .filter((entry) => entry.name.includes(urlFragment) && entry.startTime >= startTime)
            .reduce((sum, entry) => sum + entry.decodedBodySize, 0);
        return total > 0 ? total : undefined;
    }

    /**
     * Subscribes to Angular Router events to track route changes as New Relic interactions.
     *
     * On NavigationStart, starts a new interaction and records the start time.
     * On NavigationEnd or NavigationError, records a RouteChange action with the duration,
     * takes a memory snapshot, and ends the interaction.
     */
    private _setupRouteTracking(router: Router): void {
        let navStart: number;
        let interactionId: string | undefined;

        router.events.pipe(
            filter((e) =>
                e instanceof NavigationStart
                || e instanceof NavigationEnd
                || e instanceof NavigationError),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((event) => {
            if (event instanceof NavigationStart) {
                navStart = performance.now();
                interactionId = this.startInteraction(`navigate:${event.url}`);
            }
            if (event instanceof NavigationEnd || event instanceof NavigationError) {
                const duration = performance.now() - navStart;
                this.recordAction("RouteChange", {
                    url: event.url,
                    durationMs: duration,
                });
                this.recordMemorySnapshot(`route:${event.url}`);
                if (interactionId) {
                    this.endInteraction(interactionId);
                    interactionId = undefined;
                }
            }
        });
    }

    /**
     * Determines whether a log call at the given severity should be forwarded to Rollbar.
     *
     * Returns false if Rollbar was never initialized (not configured, or configured with
     * {@link LogLevel.Off}). Otherwise, compares the severity ranking of the configured
     * level against the ranking of the call's level - the call is forwarded only when the
     * configured level is at least as verbose as the call.
     *
     * @param level - The severity of the log call being considered
     * @returns True if the call should be forwarded to Rollbar
     */
    private _shouldLogToRollbar(level: LogLevel): boolean {
        const configuredLevel = this._appConfig.rollbar?.level;
        if (!this._rollbar || !configuredLevel) {
            return false;
        }
        return LOG_LEVEL_RANK[configuredLevel] >= LOG_LEVEL_RANK[level];
    }

    /**
     * Determines whether a log call at the given severity should be forwarded to New Relic.
     *
     * @param level - The severity of the log call being considered
     * @returns True if New Relic is active and the configured log level is at least as verbose as the call
     */
    private _shouldLogToNewRelic(level: LogLevel): boolean {
        const configuredLevel = this._appConfig.newrelic?.logLevel;
        if (!this._nrActive || !configuredLevel || configuredLevel === LogLevel.Off) {
            return false;
        }
        return LOG_LEVEL_RANK[configuredLevel] >= LOG_LEVEL_RANK[level];
    }

    /**
     * Serializes a log argument for a New Relic custom attribute, falling back to `String(arg)`
     * for values JSON cannot represent (circular objects, functions, symbols), truncated to the maximum length.
     *
     * @param arg - The argument to serialize
     * @returns The truncated string form of the argument
     */
    private _serializeAttribute(arg: unknown): string {
        let serialized: string | undefined;
        try {
            serialized = JSON.stringify(arg);
        }
        catch {
            serialized = undefined;
        }
        return (serialized ?? String(arg)).slice(0, LoggingService._MAX_ATTRIBUTE_LENGTH);
    }

    /**
     * Forwards a log call to New Relic Browser as a log, and reports each Error (including nested
     * original errors) with `noticeError` for error-level calls.
     *
     * Never throws and never calls {@link logError}, to avoid recursion; failures fall back to `console.error`.
     *
     * @param level - The severity of the log call
     * @param loggingArguments - The arguments passed to the log call
     */
    private _logToNewRelic(level: LogLevel, loggingArguments: Array<unknown>): void {
        if (!this._shouldLogToNewRelic(level)) {
            return;
        }
        try {
            const customAttributes: Record<string, string | number> = {
                appVersion: this._appConfig.appVersion,
                route: this._router?.url ?? location.pathname
            };
            const messages: Array<string> = [];
            loggingArguments.forEach((arg, index) => {
                if (typeof arg === "string") {
                    messages.push(arg);
                }
                else if (arg instanceof Error) {
                    messages.push(arg.message);
                }
                else if (arg !== undefined) {
                    customAttributes[`arg${index}`] = this._serializeAttribute(arg);
                }
            });
            newrelic.log(messages.join(" "), {
                level: LoggingService._NR_LEVEL[level],
                customAttributes
            });
            if (level === LogLevel.Error) {
                this._processLoggingArguments(loggingArguments)
                    .filter((arg): arg is Error => arg instanceof Error)
                    .forEach((error) => newrelic.noticeError(error, customAttributes));
            }
        }
        catch (error) {
            console.error("New Relic logging failed", error);
        }
    }

    /**
     * Processes logging arguments to extract nested errors and convert to Rollbar format.
     *
     * Iterates through all provided arguments and recursively extracts original errors
     * from CoreUiException objects to ensure complete error context is logged.
     *
     * @param loggingArguments - Array of arguments to process
     * @returns Processed array of Rollbar.LogArgument objects suitable for Rollbar
     */
    private _processLoggingArguments(loggingArguments: Array<unknown>): Array<Rollbar.LogArgument> {
        let fields: Array<Rollbar.LogArgument> = [];
        for (const arg of loggingArguments) {
            fields.push(arg as Rollbar.LogArgument);
            fields = fields.concat(this._processOriginalErrors(arg));
        }
        return fields;
    }

    /**
     * Recursively extracts original errors from CoreUiException objects.
     *
     * CoreUiException objects can contain nested original errors. This method
     * recursively traverses the error chain to extract all original errors
     * for comprehensive logging.
     *
     * @param arg - Argument to check for original errors
     * @returns Array of extracted original errors
     */
    private _processOriginalErrors(arg: unknown): Array<unknown> {

        let fields: Array<unknown> = [];
        const originalError = (arg as CoreUiException)?.originalError;
        if (originalError) {
            fields.push(originalError);
            fields = fields.concat(this._processOriginalErrors(originalError));
        }
        return fields;
    }
}
